import {
  Info as InfoIcon,
} from '@mui/icons-material'
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { CommonTable, Search, CommonModal } from '../common'
import {
  downloadColumns,
  downloadSearchFields,
} from '../../constants/downloadsColumn'
import { getDowloadsData, downloadFileById } from "../../api/downloads.js"
import DetailsModal from './DetailsModal'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import {getValidCriteria,sanitizeText} from "../../constants/index.js"
import DOMPurify from 'dompurify'
import DownloadIcon from '../../assets/icons/DownloadIcon.jsx'


const Download = ({ roleConfig = { canEdit: true } }) => {
  const [downloads, setDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedItems, setSelectedItems] = useState([])
  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: downloadSearchFields[0]?.id || '',
      value: '',
    },
  ])
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [downloadSearchData, setDownloadSearchData] = useState([])
  const [startLoading, setStartLoading] = useState(false)
  const { showSnackbar } = useSnackbar()
  // Function to load downloads (mock data for now)
  const loadDownloads = async (page = 1, pageSize = itemsPerPage) => {
    setLoading(true)
    try {
      const payload = {
        pageFilter: {
          currentPage: page,
          includeCount: true,
          pageSize: pageSize,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: [],
        entityTANs: [],
        months: [],
        year: '',
      }
      const resp = await getDowloadsData(payload)

      setDownloads(resp.data.data.content)
      setTotalItems(resp.data.data.totalElements)
    } catch (error) {
      console.error('Error loading downloads:')
      setDownloads([])
    } finally {
      setLoading(false)
    }
  }

  // Load downloads on component mount
  useEffect(() => {
    loadDownloads(currentPage, itemsPerPage, getValidCriteria(searchCriteria))
    const intervalId = setInterval(() => {
      loadDownloads(currentPage, itemsPerPage, getValidCriteria(searchCriteria))
    }, 60000)
    return () => clearInterval(intervalId)
  }, [])



  const handleSearch = event => {
    setAnchorEl(event.currentTarget)
    setSearchOpen(true)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadDownloads(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadEntities(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  const handlePageChange = async newPage => {
    setCurrentPage(newPage)
    await loadDownloads(newPage, itemsPerPage)
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    await loadDownloads(1, newItemsPerPage)
  }

  const handleSelectItem = itemId => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId)
      }
      return [...prev, itemId]
    })
  }



  const handleSelectAll = event => {
    if (event.target.checked) {
      const allIds = downloads.map(item => item.uuid)
      setSelectedItems(allIds)
    } else {
      setSelectedItems([])
    }
  }

 const handleDownload = async () => {
  if (!selectedItems.length) {
    showSnackbar("No items selected", "info");
    return;
  }

  setStartLoading(true);
  const anchorOrigin = { vertical: "bottom", horizontal: "left" };
  showSnackbar("Download request initiated", "info", anchorOrigin);

  try {
    const filesToDownload = downloads
      .filter(item => selectedItems.includes(item.uuid))
      .map(item => ({
        downloadId: item.downloadId,
        reportName: sanitizeText(item.reportName || `file-${item.downloadId}`),
      }));

    const downloadIds = filesToDownload.map(f => f.downloadId);
    const resp = await downloadFileById(downloadIds);

    if (!resp?.data?.success && !Array.isArray(resp.data)) {
      const errMsg = sanitizeText(resp?.data?.errors?.[0]?.message || "Download failed");
      showSnackbar(errMsg, "error");
      return;
    }

    const urls = Array.isArray(resp.data?.data)
      ? resp.data.data
      : Array.isArray(resp.data)
      ? resp.data
      : [resp.data];

    for (let i = 0; i < urls.length; i++) {
      const urlItem = urls[i];
      const fileUrl = urlItem?.url || urlItem?.data || urlItem?.fileUrl || urlItem;

      if (!fileUrl) {
        showSnackbar(`File ${filesToDownload[i].reportName} not ready`, "warning");
        continue;
      }

      const isSafeS3Url =
        /^https:\/\/[a-zA-Z0-9.-]+\.s3[.-][a-z0-9-]+\.amazonaws\.com\/[a-zA-Z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+$/.test(fileUrl);

      if (!isSafeS3Url) {
        const safeMsg = `Unsafe or invalid S3 URL for ${sanitizeText(filesToDownload[i].reportName)}`;
        showSnackbar(safeMsg, "error");
        continue;
      }

      // ✅ Sanitize URL again before assigning
      const safeUrl = DOMPurify.sanitize(fileUrl, { ALLOWED_URI_REGEXP: /^https:\/\/.*/ });

      const a = document.createElement("a");
      a.setAttribute("href", safeUrl);
      a.setAttribute("download", `${filesToDownload[i].reportName}.zip`);
      a.setAttribute("rel", "noopener noreferrer");
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      await new Promise(resolve => setTimeout(resolve, 400));
    }

    setSelectedItems([]);
  } catch (error) {
    showSnackbar(sanitizeText(error.message) || "Something went wrong", "error");
  } finally {
    setStartLoading(false);
  }
};




  // Custom actions for each row
  const handleRowAction = download => {
    setModalOpen(true);
    setDownloadSearchData(download)
    // Add your action logic here (view details, download file, etc.)
  }

  // Custom render function for actions column
  const renderActions = (value, row) => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="View Details">
          <IconButton
            onClick={() => handleRowAction(row)}
            disabled={row.dataType === GLOBAL_CONSTANTS.COMPLIANCE_MASTER_FILE_DATA_TYPE}
            sx={{
              color: 'var(--forbrand-color)',              
            }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  // Add actions column to the download columns
  const columnsWithActions = [
    ...downloadColumns,
    {
      id: 'actions',
      label: 'Actions',
      width: '100px',
      align: 'center',
      render: renderActions,
    },
  ]

  const handleModalClose = () => {
    setModalOpen(false)
  }

  return (
    <Box
      sx={{
        padding: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
          mb: 2,
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          Download Status
        </Typography>

        {/* Right side - Action buttons */}
        {selectedItems?.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
              <IconButton
                disabled={startLoading}
                onClick={handleDownload}
              >
               <DownloadIcon/>
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

      </Box>

      {/* Table */}
      <CommonTable
        columns={columnsWithActions}
        data={downloads}
        loading={loading}
        emptyMessage="No downloads found"
        showFooter={true}
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        showItemsPerPage={true}
        showTotalItems={true}
        showPageNumbers={true}
        showActions={false} // We handle actions through custom column
        showCheckbox={true}
        selectedItems={selectedItems}
        onSelectItem={handleSelectItem}
        onSelectAll={handleSelectAll}
        stickyFooter={true}
      />

      {/* Search Modal */}
      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={downloadSearchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />

      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        title={''}
        maxWidth={'sm'}
        sx={{
          '& .MuiDialogContent-root': {
            padding: 0,
          },
          '& .MuiDialogTitle-root': {
            display: 'none',
          },
        }}
      >
        <DetailsModal onClose={handleModalClose} data={downloadSearchData} />
      </CommonModal>
    </Box>
  )
}

export default Download
